import React, { useEffect, useState } from 'react';
import {
  fetchArtifactDetail,
  fetchArtifactItemDetail,
} from '../../../services/kryspaceEntityDetailService';
import { isKryspaceRenderServiceError } from '../../../services/kryspaceRenderService';
import {
  canDecomposeImprovementItem,
  canStartActivityItem,
  canValidateImprovementItem,
  decomposeImprovement,
  improvementRefFromNotValidatedReason,
  isImprovementNotValidatedBlockReason,
  resolveDecomposeAssignedResourceRef,
  ControlBridgeDecomposeError,
} from '../../../services/controlBridgeDecomposeService';
import {
  ControlBridgeExoPanelExecutionError,
  executeExoPanelAction,
} from '../../../services/controlBridgeExoPanelExecutionService';
import { formatArtifactTypeRefDisplay } from '../../../utils/artifactTypeRefDisplay';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type {
  ArtifactDetailPayload,
  ArtifactItemDetailPayload,
} from '../../../types/kryspaceEntityDetail';
import { EntityDetailShell } from './EntityDetailShell';
import {
  EntityDetailArtifactTreeLink,
  EntityDetailChildCompanyArtifactsList,
  EntityDetailErrorState,
  EntityDetailLoadingState,
  EntityDetailRelationshipsList,
  EntityDetailExecutionSummary,
  EntityDetailStateHistoryList,
  EntityDetailStateMachineSummary,
  EntityDetailReferencedItemSection,
} from './ArtifactDetailSections';
import {
  queueItemFallbackFields,
  queueItemFallbackState,
  queueItemFallbackSubtitle,
  queueItemFallbackTitle,
  normalizeEntityDetailFields,
  type ControlBridgeEntityDetailTarget,
} from './entityDetailTargets';
import {
  resolveParentNavTarget,
  resolveStructureNavList,
  structureNavOpenKind,
  structureParentScopeKey,
} from './structureDomainNav';
import styles from '../ControlBridge.module.css';

export interface ControlBridgeEntityDetailOverlayProps {
  workspaceSlug: string;
  target: ControlBridgeEntityDetailTarget;
  queueItems?: ControlBridgeQueueItem[];
  structure?: KryspaceControlBridgeStructure;
  onClose: () => void;
  onBack?: () => void;
  onOpenItemDetail: (itemId: string, invokingLabel: string) => void;
  onOpenArtifactDetail?: (artifactInstanceId: string, invokingLabel: string) => void;
  /** Set Control Bridge sidebar / queue scope when picking a structure domain. */
  onSelectStructureScope?: (filterScope: string) => void;
  onManifestRefresh?: () => void | Promise<void>;
}

type DetailLoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'artifact'; payload: ArtifactDetailPayload }
  | { status: 'item'; payload: ArtifactItemDetailPayload };

function errorMessage(err: unknown): string {
  if (isKryspaceRenderServiceError(err)) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to load entity detail';
}

function entityDetailTargetKey(target: ControlBridgeEntityDetailTarget): string {
  return target.kind === 'artifact'
    ? `artifact:${target.artifactInstanceId}`
    : `item:${target.itemId}`;
}

function detailFieldText(
  fields: Array<{ key: string; value?: unknown }> | undefined,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const field = fields?.find((row) => row.key === key);
    if (typeof field?.value === 'string' && field.value.trim()) {
      return field.value.trim();
    }
  }
  return undefined;
}

function decomposeResourceRefForItem(
  payload: ArtifactItemDetailPayload,
  fallbackItem: ControlBridgeQueueItem | undefined,
  queueItems: ControlBridgeQueueItem[]
): string | undefined {
  const resourceAssignment =
    fallbackItem?.resourceAssignment ??
    detailFieldText(payload.fields, ['resource_assignment', 'resourceAssignment']);
  return resolveDecomposeAssignedResourceRef({
    assignedResourceRef:
      fallbackItem?.assignedResourceRef ??
      detailFieldText(payload.fields, ['assigned_resource_ref', 'assignedResourceRef']),
    resourceAssignment,
    queueItems: queueItems.length > 0 ? queueItems : fallbackItem ? [fallbackItem] : undefined,
  });
}

export const ControlBridgeEntityDetailOverlay: React.FC<ControlBridgeEntityDetailOverlayProps> = ({
  workspaceSlug,
  target,
  queueItems = [],
  structure,
  onClose,
  onBack,
  onOpenItemDetail,
  onOpenArtifactDetail,
  onSelectStructureScope,
  onManifestRefresh,
}) => {
  const [loadState, setLoadState] = useState<DetailLoadState>({ status: 'loading' });
  const [decomposeState, setDecomposeState] = useState<
    'idle' | 'loading' | 'applied' | 'preview' | 'error'
  >('idle');
  const [decomposeMessage, setDecomposeMessage] = useState<string | null>(null);
  const [startBusy, setStartBusy] = useState(false);
  const [validateBusy, setValidateBusy] = useState(false);
  const [ignoreFallbackBlockReason, setIgnoreFallbackBlockReason] = useState(false);
  const targetKey = entityDetailTargetKey(target);
  const targetKind = target.kind;
  const targetEntityId =
    target.kind === 'artifact' ? target.artifactInstanceId : target.itemId;

  useEffect(() => {
    setDecomposeState('idle');
    setDecomposeMessage(null);
    setStartBusy(false);
    setValidateBusy(false);
    setIgnoreFallbackBlockReason(false);
  }, [targetKey]);

  useEffect(() => {
    let cancelled = false;
    setLoadState({ status: 'loading' });

    const load = async () => {
      try {
        if (targetKind === 'artifact') {
          const payload = await fetchArtifactDetail(workspaceSlug, targetEntityId);
          if (!cancelled) setLoadState({ status: 'artifact', payload });
          return;
        }
        const payload = await fetchArtifactItemDetail(workspaceSlug, targetEntityId);
        if (!cancelled) setLoadState({ status: 'item', payload });
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadState({ status: 'error', message: errorMessage(err) });
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [workspaceSlug, targetKey, targetKind, targetEntityId]);

  if (target.kind === 'item' && loadState.status === 'error' && target.fallbackItem) {
    const item = target.fallbackItem;
    return (
      <EntityDetailShell
        variant="item"
        title={queueItemFallbackTitle(item)}
        subtitle={queueItemFallbackSubtitle(item)}
        stateLabel={queueItemFallbackState(item)}
        invokingLabel={target.invokingLabel}
        onClose={onClose}
        onBack={onBack}
        fields={queueItemFallbackFields(item)}
      >
        <EntityDetailErrorState message={loadState.message} />
      </EntityDetailShell>
    );
  }

  if (loadState.status === 'loading') {
    const title =
      target.kind === 'artifact'
        ? target.fallbackLabel ?? target.artifactInstanceId
        : target.fallbackItem?.title ?? target.itemId;
    const subtitle =
      target.kind === 'artifact' ? target.artifactInstanceId : target.itemId;

    return (
      <EntityDetailShell
        variant={target.kind}
        title={title}
        subtitle={subtitle}
        invokingLabel={target.invokingLabel}
        onClose={onClose}
        onBack={onBack}
      >
        <EntityDetailLoadingState />
      </EntityDetailShell>
    );
  }

  if (loadState.status === 'error') {
    const title =
      target.kind === 'artifact'
        ? target.fallbackLabel ?? target.artifactInstanceId
        : target.fallbackItem?.title ?? target.itemId;

    return (
      <EntityDetailShell
        variant={target.kind}
        title={title}
        subtitle={target.kind === 'artifact' ? target.artifactInstanceId : target.itemId}
        invokingLabel={target.invokingLabel}
        onClose={onClose}
        onBack={onBack}
      >
        <EntityDetailErrorState message={loadState.message} />
      </EntityDetailShell>
    );
  }

  if (loadState.status === 'artifact') {
    const payload = loadState.payload;
    const nav = resolveStructureNavList({
      structure,
      entityId: payload.artifactInstanceId,
      artifactTypeRef: payload.artifactTypeRef,
      childCompanyArtifacts: payload.childCompanyArtifacts ?? [],
      queueItems,
    });

    const openNavTarget = (artifactInstanceId: string) => {
      if (nav?.openMode === 'work') {
        onOpenItemDetail(artifactInstanceId, 'Eligible work');
        return;
      }
      onSelectStructureScope?.(artifactInstanceId);
      if (structureNavOpenKind(artifactInstanceId) === 'item') {
        onOpenItemDetail(artifactInstanceId, 'Structure');
        return;
      }
      onOpenArtifactDetail?.(artifactInstanceId, 'Artifact detail');
    };

    return (
      <EntityDetailShell
        variant="artifact"
        title={payload.name}
        subtitle={`${formatArtifactTypeRefDisplay(payload.artifactTypeRef)}${payload.domainLabel ? ` · ${payload.domainLabel}` : ''}`}
        stateLabel={payload.currentState}
        invokingLabel={target.invokingLabel}
        onClose={onClose}
        onBack={onBack}
        fields={normalizeEntityDetailFields(payload.fields)}
        footerPrimary={
          <EntityDetailArtifactTreeLink
            artifactInstanceId={payload.artifactInstanceId}
            workspaceSlug={workspaceSlug}
          />
        }
      >
        {payload.stateMachine ? (
          <EntityDetailStateMachineSummary stateMachine={payload.stateMachine} />
        ) : null}
        {nav ? (
          <EntityDetailChildCompanyArtifactsList
            items={nav.items}
            sectionTitle={nav.sectionTitle}
            onOpenArtifactDetail={openNavTarget}
          />
        ) : null}
        <EntityDetailRelationshipsList relationships={payload.relationships} />
      </EntityDetailShell>
    );
  }

  const payload = loadState.payload;
  const execution = payload.executionSummary;
  const showDecompose = canDecomposeImprovementItem(payload.artifactTypeRef);
  const showStart = canStartActivityItem(payload.artifactTypeRef, payload.currentState);
  const showValidateImprovement = canValidateImprovementItem(
    payload.artifactTypeRef,
    payload.currentState
  );
  const fallbackItem = target.kind === 'item' ? target.fallbackItem : undefined;
  const blockReason =
    payload.blockedReason ??
    (ignoreFallbackBlockReason ? undefined : fallbackItem?.blockedReason) ??
    undefined;
  const parentImprovementRef =
    fallbackItem?.improvementRef ??
    improvementRefFromNotValidatedReason(blockReason) ??
    undefined;
  const showValidateParent =
    !showValidateImprovement &&
    isImprovementNotValidatedBlockReason(blockReason) &&
    Boolean(parentImprovementRef);
  const itemStructureNav = resolveStructureNavList({
    structure,
    entityId: payload.itemId,
    artifactTypeRef: payload.artifactTypeRef,
    queueItems,
  });
  const parentNav = resolveParentNavTarget({
    entityId: payload.itemId,
    parentArtifactInstanceId: payload.parentArtifactInstanceId,
    parentArtifactLabel: payload.parentArtifactLabel,
    structure,
    allowControlBridgeRootFallback: target.invokingLabel === 'ExoPanel',
  });

  const openNavTarget = (artifactInstanceId: string) => {
    if (itemStructureNav?.openMode === 'work') {
      onOpenItemDetail(artifactInstanceId, 'Eligible work');
      return;
    }
    onSelectStructureScope?.(artifactInstanceId);
    if (structureNavOpenKind(artifactInstanceId) === 'item') {
      onOpenItemDetail(artifactInstanceId, 'Structure');
      return;
    }
    onOpenArtifactDetail?.(artifactInstanceId, 'Artifact detail');
  };

  const openParentTarget = (parentId: string) => {
    onSelectStructureScope?.(structureParentScopeKey(parentId, structure));
    if (structureNavOpenKind(parentId) === 'item') {
      onOpenItemDetail(parentId, 'Parent artifact');
      return;
    }
    onOpenArtifactDetail?.(parentId, 'Parent artifact');
  };

  const runDecompose = async (apply: boolean) => {
    setDecomposeState('loading');
    setDecomposeMessage(null);
    try {
      const result = await decomposeImprovement(workspaceSlug, {
        improvementRef: payload.itemId,
        apply,
        name: payload.name,
        assignedResourceRef: decomposeResourceRefForItem(payload, fallbackItem, queueItems),
      });
      const patchId =
        result.artifactPatch && typeof result.artifactPatch.item_id === 'string'
          ? result.artifactPatch.item_id
          : null;
      if (result.applied && patchId) {
        setDecomposeState('applied');
        setDecomposeMessage(`Activity ${patchId} created.`);
        await onManifestRefresh?.();
        return;
      }
      setDecomposeState('preview');
      setDecomposeMessage(
        patchId
          ? `Preview ready for activity ${patchId}. Use Create activity to persist.`
          : 'Preview ready. Use Create activity to persist.'
      );
    } catch (err: unknown) {
      setDecomposeState('error');
      setDecomposeMessage(
        err instanceof ControlBridgeDecomposeError ? err.message : 'Decompose failed'
      );
    }
  };

  const mergeItemCurrentState = (nextState: string | undefined) => {
    const next = nextState?.trim();
    if (!next) return;
    setLoadState((prev) => {
      if (prev.status !== 'item') return prev;
      if (prev.payload.currentState === next) return prev;
      return { status: 'item', payload: { ...prev.payload, currentState: next } };
    });
  };

  const reloadItemDetailQuiet = async () => {
    if (target.kind !== 'item') return;
    const next = await fetchArtifactItemDetail(workspaceSlug, target.itemId);
    setLoadState({ status: 'item', payload: next });
  };

  const runStart = async () => {
    setStartBusy(true);
    setDecomposeMessage(null);
    try {
      const result = await executeExoPanelAction(workspaceSlug, {
        itemId: payload.itemId,
        action: 'start',
      });
      mergeItemCurrentState(result.toState ?? 'ACTIVE');
      await onManifestRefresh?.();
      try {
        await reloadItemDetailQuiet();
      } catch {
        // Keep optimistic toState so Start does not return after persist.
      }
      setDecomposeState('applied');
      setDecomposeMessage(`Started → ${result.toState ?? 'ACTIVE'}.`);
    } catch (err: unknown) {
      setDecomposeState('error');
      setDecomposeMessage(
        err instanceof ControlBridgeExoPanelExecutionError ? err.message : 'Start failed'
      );
      if (
        err instanceof ControlBridgeExoPanelExecutionError &&
        err.code === 'PERSIST_MODE_NOT_PATCH'
      ) {
        try {
          await onManifestRefresh?.();
        } catch {
          // Keep persistMode error primary.
        }
      }
    } finally {
      setStartBusy(false);
    }
  };

  const runValidate = async (improvementItemId: string) => {
    setValidateBusy(true);
    setDecomposeMessage(null);
    try {
      const result = await executeExoPanelAction(workspaceSlug, {
        itemId: improvementItemId,
        action: 'validate',
      });
      if (improvementItemId === payload.itemId) {
        mergeItemCurrentState(result.toState ?? 'VALIDATED');
      } else {
        setIgnoreFallbackBlockReason(true);
      }
      await onManifestRefresh?.();
      try {
        await reloadItemDetailQuiet();
      } catch {
        // Keep optimistic toState so Validate does not return after persist.
      }
      setDecomposeState('applied');
      setDecomposeMessage(
        `Validated → ${result.toState ?? 'VALIDATED'}. Eligible work should refresh after pickup gates clear.`
      );
    } catch (err: unknown) {
      setDecomposeState('error');
      setDecomposeMessage(
        err instanceof ControlBridgeExoPanelExecutionError ? err.message : 'Validate failed'
      );
      if (
        err instanceof ControlBridgeExoPanelExecutionError &&
        err.code === 'PERSIST_MODE_NOT_PATCH'
      ) {
        try {
          await onManifestRefresh?.();
        } catch {
          // Keep persistMode error primary.
        }
      }
    } finally {
      setValidateBusy(false);
    }
  };

  const footerActions =
    showDecompose || showStart || showValidateImprovement || showValidateParent ? (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {showValidateImprovement ? (
          <button
            type="button"
            className={styles.entityDetailBack}
            data-testid="entity-detail-validate-improvement"
            disabled={validateBusy || startBusy || decomposeState === 'loading'}
            onClick={() => void runValidate(payload.itemId)}
          >
            {validateBusy ? 'Validating…' : 'Validate'}
          </button>
        ) : null}
        {showValidateParent && parentImprovementRef ? (
          <button
            type="button"
            className={styles.entityDetailBack}
            data-testid="entity-detail-validate-parent-improvement"
            disabled={validateBusy || startBusy || decomposeState === 'loading'}
            onClick={() => void runValidate(parentImprovementRef)}
          >
            {validateBusy ? 'Validating…' : 'Validate improvement'}
          </button>
        ) : null}
        {showStart ? (
          <button
            type="button"
            className={styles.entityDetailBack}
            data-testid="entity-detail-start-activity"
            disabled={startBusy || validateBusy || decomposeState === 'loading'}
            onClick={() => void runStart()}
          >
            {startBusy ? 'Starting…' : 'Start'}
          </button>
        ) : null}
        {showDecompose ? (
          <>
            <button
              type="button"
              className={styles.entityDetailBack}
              disabled={decomposeState === 'loading'}
              onClick={() => void runDecompose(false)}
            >
              Preview activity
            </button>
            <button
              type="button"
              className={styles.entityDetailBack}
              disabled={decomposeState === 'loading'}
              onClick={() => void runDecompose(true)}
            >
              Create activity
            </button>
          </>
        ) : null}
      </div>
    ) : undefined;

  return (
    <EntityDetailShell
      variant="item"
      title={payload.name}
      subtitle={
        execution?.contextLabel ?? formatArtifactTypeRefDisplay(payload.artifactTypeRef)
      }
      stateLabel={payload.currentState}
      invokingLabel={target.invokingLabel}
      onClose={onClose}
      onBack={onBack}
      fields={normalizeEntityDetailFields(payload.fields)}
      footerPrimary={footerActions}
    >
      {decomposeMessage ? (
        <p
          className={
            decomposeState === 'error' ? styles.entityDetailStatusError : styles.entityDetailStatus
          }
          role={decomposeState === 'error' ? 'alert' : 'status'}
        >
          {decomposeMessage}
        </p>
      ) : null}
      {blockReason && isImprovementNotValidatedBlockReason(blockReason) ? (
        <p className={styles.entityDetailStatus} role="status">
          Pickup blocked: parent improvement must be VALIDATED before Eligible work.
        </p>
      ) : null}
      {execution ? (
        <EntityDetailExecutionSummary
          itemLabel={execution.itemLabel}
          actionLabel={execution.actionLabel}
          contextLabel={execution.contextLabel}
        />
      ) : null}
      {payload.stateHistory && payload.stateHistory.length > 0 ? (
        <EntityDetailStateHistoryList entries={payload.stateHistory} />
      ) : null}
      {itemStructureNav ? (
        <EntityDetailChildCompanyArtifactsList
          items={itemStructureNav.items}
          sectionTitle={itemStructureNav.sectionTitle}
          onOpenArtifactDetail={openNavTarget}
        />
      ) : null}
      {payload.referencedItem ? (
        <EntityDetailReferencedItemSection
          referencedItem={payload.referencedItem}
          onOpenItemDetail={(itemId) => onOpenItemDetail(itemId, 'Referenced item')}
        />
      ) : null}
      {parentNav ? (
        <section className={styles.entityDetailSection} aria-label="Parent artifact">
          <h3 className={styles.entityDetailSectionTitle}>Parent artifact</h3>
          <div className={styles.entityDetailChildRow}>
            <div>
              <div className={styles.entityDetailChildName}>{parentNav.label}</div>
              <div className={styles.entityDetailChildMeta}>{parentNav.id}</div>
            </div>
            <button
              type="button"
              className={styles.entityDetailChildOpen}
              onClick={() => openParentTarget(parentNav.id)}
              aria-label={`Open parent ${parentNav.label}`}
            >
              →
            </button>
          </div>
        </section>
      ) : null}
    </EntityDetailShell>
  );
};
